import React, { useState } from 'react';
import demoPreview from '../assets/demo-preview.png';

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --blue: #2563EB;
    --blue-light: #3B82F6;
    --blue-pale: #EFF6FF;
    --ink: #0F172A;
    --ink-2: #1E293B;
    --muted: #475569;
    --muted-2: #94A3B8;
    --line: #E2E8F0;
    --white: #FFFFFF;
    --bg: #F8FAFC;
    --green: #059669;
    --green-pale: #ECFDF5;
  }

  .demo-root {
    min-height: 100vh;
    background: var(--bg);
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
    color: var(--ink);
    -webkit-font-smoothing: antialiased;
  }

  /* ── HERO ── */
  .demo-hero {
    position: relative;
    background: var(--ink);
    overflow: hidden;
    padding: 120px 24px 100px;
  }

  .demo-hero-grid {
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(59,130,246,0.06) 1px, transparent 1px),
      linear-gradient(90deg, rgba(59,130,246,0.06) 1px, transparent 1px);
    background-size: 48px 48px;
  }

  .demo-hero-glow {
    position: absolute;
    top: -120px;
    left: 50%;
    transform: translateX(-50%);
    width: 800px;
    height: 450px;
    background: radial-gradient(ellipse, rgba(37,99,235,0.25) 0%, transparent 70%);
    pointer-events: none;
  }

  .demo-hero-inner {
    position: relative;
    max-width: 860px;
    margin: 0 auto;
    text-align: center;
  }

  .demo-hero-title {
    font-size: clamp(38px, 6vw, 64px);
    font-weight: 800;
    line-height: 1.08;
    color: var(--white);
    margin-bottom: 24px;
    letter-spacing: -0.025em;
  }

  .demo-hero-title span {
    background: linear-gradient(135deg, #60A5FA, #2563EB);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .demo-hero-sub {
    font-size: 17px;
    color: #94A3B8;
    line-height: 1.7;
    max-width: 540px;
    margin: 0 auto 28px;
    font-weight: 400;
  }

  .demo-hero-tagline {
    font-size: 15px;
    font-weight: 500;
    color: #CBD5E1;
    letter-spacing: 0.02em;
    margin-top: 0;
  }

  /* ── VIDEO ── */
  .demo-video-wrap {
    position: relative;
    max-width: 860px;
    margin: 0 auto;
    padding: 0 24px;
    margin-top: -48px;
    z-index: 10;
  }

  .demo-video-shell {
    background: var(--ink-2);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 24px;
    overflow: hidden;
    box-shadow:
      0 0 0 1px rgba(255,255,255,0.04),
      0 24px 60px rgba(0,0,0,0.35),
      0 8px 20px rgba(0,0,0,0.15);
  }

  .demo-video-bar {
    background: rgba(15,23,42,0.92);
    padding: 14px 20px;
    display: flex;
    align-items: center;
    gap: 14px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
  }

  .demo-video-dots {
    display: flex;
    gap: 8px;
  }

  .demo-video-dot {
    width: 10px; height: 10px;
    border-radius: 50%;
  }

  .demo-video-url {
    flex: 1;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 6px;
    padding: 6px 14px;
    font-size: 11px;
    color: #64748B;
    font-family: 'Inter', monospace;
    font-weight: 500;
  }

  .demo-video-frame {
    position: relative;
    width: 100%;
    padding-bottom: 56.25%;
    background: #000;
  }

  .demo-video-frame iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: none;
  }

  /* Placeholder avec fallback d'images */
  .demo-video-placeholder {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    gap: 24px;
    /* Le fond sera fourni dynamiquement via backgroundImage inline */
  }

  .demo-video-placeholder::before {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(15,23,42,0.6);
    backdrop-filter: blur(2px);
    z-index: 0;
  }

  .demo-play-btn {
    width: 72px; height: 72px;
    border-radius: 50%;
    background: var(--blue);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s;
    box-shadow: 0 0 0 0 rgba(37,99,235,0.4);
    animation: ring 2s infinite;
    position: relative;
    z-index: 1;
  }

  @keyframes ring {
    0% { box-shadow: 0 0 0 0 rgba(37,99,235,0.4); }
    70% { box-shadow: 0 0 0 20px rgba(37,99,235,0); }
    100% { box-shadow: 0 0 0 0 rgba(37,99,235,0); }
  }

  .demo-play-btn:hover {
    transform: scale(1.08);
  }

  .demo-play-btn svg {
    width: 28px; height: 28px;
    fill: white;
    margin-left: 4px;
    position: relative;
    z-index: 1;
  }

  .demo-placeholder-txt {
    font-size: 14px;
    color: rgba(255,255,255,0.8);
    font-weight: 400;
    position: relative;
    z-index: 1;
    text-shadow: 0 2px 4px rgba(0,0,0,0.5);
  }

  /* ── BODY ── */
  .demo-body {
    max-width: 960px;
    margin: 0 auto;
    padding: 80px 24px 100px;
  }

  .demo-label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--blue-light);
    margin-bottom: 36px;
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .demo-label::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--line);
  }

  /* ── FEATURES GRID ── */
  .demo-features {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
    margin-bottom: 80px;
  }

  @media (max-width: 700px) {
    .demo-features { grid-template-columns: 1fr; }
  }

  @media (min-width: 701px) and (max-width: 960px) {
    .demo-features { grid-template-columns: 1fr 1fr; }
  }

  .demo-feat {
    background: var(--white);
    border: 1px solid var(--line);
    border-radius: 20px;
    padding: 32px 28px;
    transition: box-shadow 0.3s ease, transform 0.3s ease, border-color 0.3s;
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .demo-feat:hover {
    box-shadow: 0 12px 36px rgba(0,0,0,0.06);
    transform: translateY(-4px);
    border-color: #BFDBFE;
  }

  .demo-feat-icon {
    width: 48px; height: 48px;
    border-radius: 14px;
    background: var(--blue-pale);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
    color: var(--ink);
  }

  .demo-feat-num {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.08em;
    color: var(--muted-2);
    margin-bottom: 12px;
    text-transform: uppercase;
  }

  .demo-feat-title {
    font-size: 16px;
    font-weight: 700;
    color: var(--ink);
    margin-bottom: 10px;
    line-height: 1.3;
  }

  .demo-feat-desc {
    font-size: 14px;
    color: var(--muted);
    line-height: 1.65;
    font-weight: 400;
  }

  /* ── CTA ── */
  .demo-cta-wrap {
    background: var(--ink);
    border-radius: 24px;
    padding: 64px 56px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 40px;
    flex-wrap: wrap;
    position: relative;
    overflow: hidden;
    margin-bottom: 48px;
  }

  .demo-cta-wrap::before {
    content: '';
    position: absolute;
    right: -60px; top: -60px;
    width: 320px; height: 320px;
    background: radial-gradient(circle, rgba(59,130,246,0.2) 0%, transparent 70%);
    pointer-events: none;
  }

  .demo-cta-title {
    font-size: 28px;
    font-weight: 800;
    color: var(--white);
    margin-bottom: 10px;
    letter-spacing: -0.02em;
  }

  .demo-cta-sub {
    font-size: 15px;
    color: #64748B;
    font-weight: 400;
  }

  .demo-cta-btns {
    display: flex;
    gap: 14px;
    flex-wrap: wrap;
    flex-shrink: 0;
    align-items: center;
  }

  .demo-btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    background: var(--blue);
    color: var(--white);
    border: none;
    border-radius: 999px;
    padding: 14px 28px;
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    text-decoration: none;
    transition: background 0.2s, transform 0.2s, box-shadow 0.2s;
    white-space: nowrap;
  }

  .demo-btn-primary:hover {
    background: #1D4ED8;
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(37,99,235,0.35);
  }

  .demo-btn-secondary {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    background: rgba(255,255,255,0.06);
    color: var(--white);
    border: 1px solid rgba(255,255,255,0.12);
    border-radius: 999px;
    padding: 14px 28px;
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    text-decoration: none;
    transition: background 0.2s, transform 0.2s;
    white-space: nowrap;
  }

  .demo-btn-secondary:hover {
    background: rgba(255,255,255,0.12);
    transform: translateY(-2px);
  }

  /* ── STATUS BAR ── */
  .demo-status {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 40px;
    flex-wrap: wrap;
    padding: 28px 32px;
    background: var(--white);
    border: 1px solid var(--line);
    border-radius: 18px;
  }

  .demo-status-item {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
    color: var(--muted);
    font-weight: 500;
  }

  .demo-status-dot {
    width: 8px; height: 8px;
    border-radius: 50%;
    background: var(--green);
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(0.85); }
  }

  .demo-status-item strong {
    color: var(--ink);
    font-weight: 600;
  }

  .demo-lang-flags {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    color: var(--muted);
    margin-left: auto;
  }

  @media (max-width: 640px) {
    .demo-hero { padding: 100px 20px 80px; }
    .demo-body { padding: 60px 20px 80px; }
    .demo-cta-wrap { padding: 48px 28px; }
    .demo-cta-title { font-size: 24px; }
    .demo-video-wrap { padding: 0 16px; }
    .demo-feat { padding: 28px 24px; }
    .demo-status { gap: 20px; padding: 24px 20px; }
  }

  @media (max-width: 480px) {
    .demo-btn-primary,
    .demo-btn-secondary {
      padding: 12px 20px;
      font-size: 13px;
      gap: 6px;
    }
    .demo-cta-btns { width: 100%; flex-direction: column; align-items: stretch; }
  }
`;

/* Icônes vectorielles noires (style Google / Stripe) */
const ICONS = {
  home: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),
  dashboard: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
    </svg>
  ),
  analytics: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  ),
  admin: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
  collaboration: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  brain: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.08-3.6A2.5 2.5 0 0 1 4.5 12a2.5 2.5 0 0 1 2.5-2.5c.5 0 .98.15 1.38.4A2.5 2.5 0 0 1 9.5 2z" />
      <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.08-3.6A2.5 2.5 0 0 0 19.5 12a2.5 2.5 0 0 0-2.5-2.5c-.5 0-.98.15-1.38.4A2.5 2.5 0 0 0 14.5 2z" />
    </svg>
  ),
  play: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M8 5v14l11-7z" />
    </svg>
  ),
};

const FEATURES = [
  {
    icon: ICONS.home,
    num: '01',
    title: "Page d'accueil des événements",
    desc: "Tableau centralisé des événements en temps réel. Suivi des activités, notifications et fil d'actualité communautaire.",
  },
  {
    icon: ICONS.dashboard,
    num: '02',
    title: "Tableau de bord utilisateur",
    desc: "Vue personnalisée de chaque utilisateur : contributions, historique de participation, score d'impact et statistiques.",
  },
  {
    icon: ICONS.analytics,
    num: '03',
    title: "Pages d'analyse",
    desc: "Visualisation approfondie des données collectives. Graphiques, tendances et insights générés par l'IA.",
  },
  {
    icon: ICONS.admin,
    num: '04',
    title: "Dashboard administrateur",
    desc: "Panneau de contrôle complet : gestion des utilisateurs, modération, configuration des paramètres et supervision globale.",
  },
  {
    icon: ICONS.collaboration,
    num: '05',
    title: "Participation collective",
    desc: "Mécanismes de contribution, votes et feedback des utilisateurs intégrés directement dans les flux de décision.",
  },
  {
    icon: ICONS.brain,
    num: '06',
    title: "Mesure d'impact par l'IA",
    desc: "Algorithmes d'intelligence artificielle qui analysent et quantifient l'impact des données sur les services et retours des entreprises.",
  },
];

const YOUTUBE_ID = '1qbwKk9Hn7A'; // ← Remplacez par votre véritable ID

// Système de fallback intelligent pour la miniature vidéo
const getPreviewImages = (videoId) => {
  const images = [demoPreview]; // Utilise la variable importée
  if (videoId) {
    images.push(`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`);
    images.push(`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`);
  }
  return images;
};

export default function DemoPage() {
  const ytUrl = YOUTUBE_ID
    ? `https://www.youtube.com/embed/${YOUTUBE_ID}?rel=0&modestbranding=1&color=white`
    : null;

  const images = getPreviewImages(YOUTUBE_ID);
  const [imgIndex, setImgIndex] = useState(0);
  const currentImage = images[imgIndex];

  const handleImageError = () => {
    setImgIndex(prev => Math.min(prev + 1, images.length - 1));
  };

  return (
    <>
      <style>{STYLES}</style>
      <div className="demo-root">

        {/* ── HERO ── */}
        <div className="demo-hero">
          <div className="demo-hero-grid" />
          <div className="demo-hero-glow" />
          <div className="demo-hero-inner">
            <h1 className="demo-hero-title">
              Découvrez <span>PichAI</span><br />en action
            </h1>
            <p className="demo-hero-sub">
              Plateforme d'intelligence collective haïtienne combinant la participation des utilisateurs et l'IA pour mesurer et amplifier l'impact des données.
            </p>
            <p className="demo-hero-tagline">
              AI-powered civic intelligence platform for Haiti
            </p>
          </div>
        </div>

        {/* ── VIDEO PLAYER ── */}
        <div className="demo-video-wrap">
          <div className="demo-video-shell">
            <div className="demo-video-bar">
              <div className="demo-video-dots">
                <div className="demo-video-dot" style={{ background: '#FF5F57' }} />
                <div className="demo-video-dot" style={{ background: '#FFBD2E' }} />
                <div className="demo-video-dot" style={{ background: '#28CA41' }} />
              </div>
              <div className="demo-video-url">pichai.tech/demo</div>
            </div>
            <div className="demo-video-frame">
              {ytUrl ? (
                <iframe
                  src={ytUrl}
                  title="PichAI Démonstration"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <div
                  className="demo-video-placeholder"
                  style={{
                    backgroundImage: `url(${currentImage})`,
                  }}
                >
                  {/* Image cachée pour détecter les échecs de chargement */}
                  <img
                    src={currentImage}
                    onError={handleImageError}
                    style={{ display: 'none' }}
                    alt=""
                  />
                  <div className="demo-play-btn">
                    <svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                  </div>
                  <p className="demo-placeholder-txt">Vidéo de démonstration — disponible bientôt</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── BODY ── */}
        <div className="demo-body">
          <div className="demo-label">Ce que vous allez voir</div>
          <div className="demo-features">
            {FEATURES.map((f) => (
              <div key={f.num} className="demo-feat">
                <div className="demo-feat-icon">{f.icon}</div>
                <div className="demo-feat-num">{f.num}</div>
                <div className="demo-feat-title">{f.title}</div>
                <div className="demo-feat-desc">{f.desc}</div>
              </div>
            ))}
          </div>

          <div className="demo-cta-wrap">
            <div>
              <div className="demo-cta-title">Voir la démo complète</div>
              <div className="demo-cta-sub">Disponible sur le site et sur YouTube — deux liens, zéro interruption.</div>
            </div>
            <div className="demo-cta-btns">
              <a
                href={YOUTUBE_ID ? `https://youtube.com/watch?v=${YOUTUBE_ID}` : '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="demo-btn-primary"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
                </svg>
                Voir sur YouTube
              </a>
              <a href="https://pichai.tech" target="_blank" rel="noopener noreferrer" className="demo-btn-secondary">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/>
                </svg>
                Lancer la plateforme
              </a>
            </div>
          </div>

          <div className="demo-status">
            <div className="demo-status-item">
              <span className="demo-status-dot" />
              <span>Plateforme <strong>en ligne</strong></span>
            </div>
            <div className="demo-status-item">
              <span>{ICONS.play}</span>
              <span>Démo <strong>disponible sur YouTube</strong></span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}